import React, { Component, createContext, useEffect, useState } from 'react';
import { IconButton, Snackbar } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { CloseIcon, ExclamationCircleIcon, TickCircleIcon } from '../components/IconIndex';

const { Provider, Consumer } = createContext({ message: '' });

let globalSnackbarMessage;

// Call this to show a snackbar from anywhere in the code
export function setSnackbarMessage(message, success = false, snackbarDuration = 5000, snackbarException = false) {
    globalSnackbarMessage(message, success, snackbarDuration, snackbarException);
}

export class SnackbarMessageProvider extends Component {
    _isMounted = false;

    constructor(props) {
        super(props);

        globalSnackbarMessage = this.setSnackbarMessage;

        this.state = {
            snackbarMessage: '',
            snackbarHappy: false,
            snackbarDuration: 5000,
            setSnackbarMessage: this.setSnackbarMessage
        };
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    setSnackbarMessage = (
        snackbarMessage,
        snackbarHappy = false,
        snackbarDuration = 5000,
        snackbarException = false
    ) => {
        if (this._isMounted) {
            this.setState({
                snackbarMessage,
                snackbarHappy,
                snackbarDuration,
                snackbarException
            });
        }
    };

    render() {
        return <Provider value={this.state}>{this.props.children}</Provider>;
    }
}

export const SnackbarMessageConsumer = props => {
    return <Consumer>{contextValue => <StyledSnackbarUI context={contextValue} {...props} />}</Consumer>;
};

const SnackbarUI = ({ context, classes }) => {
    const { snackbarMessage, setSnackbarMessage, snackbarHappy, snackbarDuration, snackbarException } = context || {};

    const [renderInfo, setRenderInfo] = useState({
        snackbarMessage: '',
        snackbarHappy: true,
        snackbarException: null
    });

    useEffect(() => {
        let isMounted = true;
        !!snackbarMessage &&
        !!isMounted &&
        setRenderInfo({
            snackbarMessage,
            snackbarHappy,
            snackbarException
        });
        return () => {
            isMounted = false;
        };
    }, [snackbarMessage, snackbarHappy, snackbarException]);

    const open = !!snackbarMessage;

    const snackbarExceptionText =
        !!renderInfo.snackbarException &&
        (Array.isArray(renderInfo.snackbarException) ? (
            <ul style={{ padding: 0 }}>
                {renderInfo.snackbarException.map((e, i) => (
                    <li key={i}>{e.message}</li>
                ))}
            </ul>
        ) : (
            renderInfo.snackbarException.message
        ));

    const messageContent = (
        <span id="message-id" className={classes.message}>
            {renderInfo.snackbarHappy ? (
                <TickCircleIcon style={{ color: 'white' }} />
            ) : (
                <ExclamationCircleIcon style={{ color: 'white' }} />
            )}
            <span>
                {renderInfo.snackbarMessage}
                {!!snackbarExceptionText && (
                    <>
                        <br />
                        <small>{snackbarExceptionText}</small>
                    </>
                )}
            </span>
        </span>
    );

    const handleCloseSnackbar = (event, reason) =>
        reason !== 'clickaway' && setSnackbarMessage && setSnackbarMessage('');

    return (
        <Snackbar
            key="global-snackbar"
            message={messageContent}
            open={open}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            action={
                <IconButton onClick={handleCloseSnackbar}>
                    <CloseIcon style={{ color: 'white' }} />
                </IconButton>
            }
            autoHideDuration={snackbarDuration || 5000}
            onClose={handleCloseSnackbar}
            className={classes.snackbar}
            classes={{
                root: !!renderInfo.snackbarMessage
                    ? !!renderInfo.snackbarHappy
                        ? classes.snackbarHappy
                        : classes.snackbarSad
                    : null
            }}
        />
    );
};

const styles = ({ spacing, palette }) => ({
    root: {
        /* just a stub for snackbar classes to work */
    },
    snackbar: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        bottom: 0,
        '& > div': {
            paddingBottom: spacing(2),
            borderRadius: '6px 6px 0 0',
            flexWrap: 'nowrap',
            maxHeight: '50vh',
            overflow: 'hidden',
            overflowY: 'auto',
            alignItems: 'start',
            '& > div:last-of-type': {
                paddingLeft: 0
            }
        }
    },
    snackbarHappy: { '& > div': { background: palette.validation.good } },
    snackbarSad: { '& > div': { background: palette.validation.error } },
    message: {
        display: 'flex',
        //alignItems: 'center',
        '& > span': {
            fontSize: '1.25em',
            marginLeft: spacing(2),
            marginRight: spacing(1),
            '& small': {
                fontFamily: 'monospace',
                display: 'block',
                borderTop: '1px dotted white',
                paddingTop: spacing(2),
                marginTop: spacing(2)
            }
        }
    }
});
const StyledSnackbarUI = withStyles(styles)(SnackbarUI);

export function withSnackbarMessage(C) {
    return props => <Consumer>{value => <C {...value} {...props} />}</Consumer>;
}
